// Page 2 of the agreement: General Terms & Conditions plus the
// Notice of Cancellation section.
import { SignaturePad } from "@/components/forms/SignaturePad";
import { InlineField, SectionTitle } from "./AgreementFields";
import { Label } from "@/components/ui/label";
import type { RBAgreementData } from "./agreementTypes";

interface Props {
  data: RBAgreementData;
  update: <K extends keyof RBAgreementData>(k: K, v: RBAgreementData[K]) => void;
}

export function AgreementPage2({ data, update }: Props) {
  return (
    <div className="bg-white text-neutral-900 px-12 py-12 shadow-sm rounded-lg print:shadow-none print:rounded-none">
      <h1 className="text-2xl font-extrabold tracking-tight border-b-4 border-neutral-900 pb-2 mb-6">
        General Terms and Conditions of Agreement
      </h1>

      <div className="text-xs leading-relaxed space-y-4">
        <div>
          <h3 className="font-bold text-sm mt-3 mb-1">CONTRACT MODIFICATION</h3>
          <p>
            Customer agrees that any future or additional modification of these terms, including additions
            to the contract scope of work, shall be made clearly in writing on a change order form, or the
            equivalent thereof, and signed and dated by Customer. Change order information includes any
            contract scope of work changes including, but not limited to, pricing, material style or
            colors, change in type of work to be completed (additions or subtractions).
          </p>
        </div>

        <div>
          <h3 className="font-bold text-sm mt-3 mb-1">PAYMENT</h3>
          <p>
            Total payment from Customer shall be set forth as listed on Contract for Roof Replacement.
            Please note that "Per Insurance Scope" or "Insurance Scope" listed as payment on contract, if
            applicable, indicates that payment will be received according to the amount equaling the
            Replacement Cost Value (RCV) as indicated on the corresponding Insurance Scope. Payment also
            includes any supplemental dollars issued by insurance for work performed by RB RESTORATION
            BUILDERS, as well as any pricing changes that may exist by way of work change orders, as
            mentioned above. Moneys owed to Contractor shall not be withheld by reason of any damage or
            claim against Contractor covered by liability or property damage insurance maintained by
            Contractor or claims covered under builder's risk insurance.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-sm mt-3 mb-1">NATURE OF WORK</h3>
          <p>
            Contractor shall furnish labor and materials necessary to perform the work described herein or
            in related contract documents. Contractor does not provide design, engineering, consulting or
            architectural services. It is the Customer's responsibility to retain a licensed architect or
            engineer to determine proper design and code compliance of the structure, including
            determination as to whether and what type of a vapor or air retarder is needed, drainage and
            wind uplift requirements. Customer acknowledges that the price of the work to be performed
            anticipates the use of heavy equipment and/or trucks to roof-top materials. It is possible that
            the driveway, curbs, or walkways may be cracked or damaged because of the weight of the
            equipment or trucks. Accordingly, Contractor disclaims liability for any cracks or damages
            caused to the driveway, curbs or walkways. If customer would prefer the contractor to hand-lift
            the materials, the contract price will need to be increased to reflect the additional labor
            cost.
          </p>
          <p className="mt-2">
            – Contractor is not responsible for and will not warranty EXISTING skylights not replaced
            during re-roof. Customer accepts full responsibility for EXISTING skylights and any problems or
            issues that may develop.
          </p>
          <p>
            – Contractor will not be responsible for the repair of Gas Lines in the attic that were
            improperly installed within 2 feet of the roof decking.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-sm mt-3 mb-1">SAFETY</h3>
          <p>
            Customer agrees to exercise extreme caution in and about the property where work is being
            performed, for the duration of the project. Customers will make any persons in and about said
            property aware of any hazards that may exist in relation to the type of work being performed
            such as, but not limited to, falling debris, moving vehicles, the possibility for the existence
            of nails and/or other debris on or about the property. Such persons must take precautions,
            accordingly, including securing all pets and children. Customer shall notify Contractor of any
            hazards remaining on or about the property within 24 hours of noticing hazard. Contractor will
            immediately make arrangements to have hazard removed. Contractor is held harmless if not
            noticed of hazards. Contractor will perform all work keeping the safety of the Customer and
            nearby persons in mind. It is specifically noted that the Contractor shall make every
            reasonable effort to remove such hazards upon completion of work (including using a magnet
            sweep around the completed project area), however, Customer shall take notice that for an
            undetermined period of time, nails or screws may remain present on the property due to the
            large scope of the project. If nails or screws remain on or about the property, Customer shall
            notify Contractor within 24 hours of noticing these items. Contractor will then make
            arrangements to conduct additional magnet sweeps in an effort to remove remaining nails or
            screws.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-sm mt-3 mb-1">SCHEDULE</h3>
          <p>
            Work will be performed in a timely manner, as is applicable to the type of work being
            performed. Contractor shall request material distributors to deliver materials as soon as
            possible, but is not able to take responsibility for delay in material delivery, especially
            regarding specialty ordered products. Any failure or delay by a party in the performance of its
            obligations under this Contract is not a default or breach of the Contract or a ground for
            termination under this Contract to the extent the failure or delay is due to elements of nature
            or Acts of God, acts of war, terrorism, riots, revolutions, pandemics, medical emergencies that
            have resulted in a local, state or federal state of emergency, Coronavirus or similar viruses
            or illnesses requiring quarantine, strikes or other factors beyond the reasonable control of a
            party (each, a "Force Majeure Event"). The party failing or delaying due to a Force Majeure
            Event agrees to give notice to the other party which describes the Force Majeure Event and
            includes a good faith estimate as to the impact of the Force Majeure Event upon its
            responsibilities under this Contract, including, but not limited to, any scheduling changes.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-sm mt-3 mb-1">TIMING OF PERFORMANCE</h3>
          <p>
            Customer's signing of this Contract authorizes Contractor to begin performing the requested
            work after 3 days have passed following the Date of Contract or Insurance Approval Date, if
            applicable. Once work begins, Customer is responsible for full payment according to the
            Contract unless Customer exercises Customer's Right to Cancel, as described herein.
            Furthermore, should Customer cancel the Contract, Customer will still remain responsible for
            paying Contractor a reasonable and customary amount for any services performed up to the time
            of cancellation.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-sm mt-3 mb-1">EFFECT OF DEFAULT (NON-PAYMENT)</h3>
          <p>
            Should Customer fail to pay for services and/or materials provided to Customer by Contractor
            according to the terms herein, Contractor may seek recovery for damages through collections,
            lien(s), or through the courts. Customer agrees to be solely responsible for any fees
            associated with recovery of said damages, including reasonable attorney fees.
          </p>
          <p className="mt-2">
            Customer agrees to allow Contractor rights to take photos or videos of project and share them
            in advertising/social media. Customer opts out of allowing Contractor rights to share photos of
            property.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-sm mt-3 mb-1">RIGHT TO CANCEL</h3>
          <p>
            Customer may cancel this Contract at any time before midnight on the 3rd business day after
            having received written notification from the insurer that the claim is not a covered loss
            under the customer's insurance policy. If customer is notified by the insurer that the claim is
            not a covered loss under the insurance policy, the customer may cancel this contract without
            penalty or monetary obligation before midnight of the third business day after having received
            notice from the insurer. This right to cancel is in addition to any other rights of
            cancellation existing under state or federal law or regulation. In the event that any part of
            this contract is determined to be unlawful or unenforceable, the remainder of this contract
            shall continue in full force and effect. This Contract shall be governed and construed in
            accordance with the applicable laws of the state of North Carolina.
          </p>
        </div>
      </div>

      <SectionTitle>Notice of Cancellation</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <InlineField label="Date of Contract:" type="date" value={data.contract_date} onChange={(v) => update("contract_date", v)} />
        <InlineField label="Customer's Name:" value={data.cancel_customer_name} onChange={(v) => update("cancel_customer_name", v)} />
        <InlineField label="PH#:" value={data.cancel_phone} onChange={(v) => update("cancel_phone", v)} />
        <InlineField label="Address:" value={data.cancel_address} onChange={(v) => update("cancel_address", v)} className="md:col-span-2" />
      </div>

      <p className="text-xs text-neutral-800 leading-relaxed mt-4">
        To cancel this Contract, mail or otherwise deliver a signed and dated copy of this cancellation
        notice, or any other signed and dated written notice of cancellation, to{" "}
        <strong>6700 Orr Rd, Charlotte, NC, USA</strong> or e-mail a notice of cancellation to{" "}
        <strong>rb@restorationbuilders.com</strong> not later than midnight of the third day after notice
        is received from the insurer, if applicable per the above, or from the date of this original
        contract.
      </p>

      <div className="mt-5 border-t pt-4">
        <p className="text-sm font-semibold mb-2">I HEREBY CANCEL THIS TRANSACTION:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InlineField
            label="Date:"
            type="date"
            value={data.cancel_signed_date}
            onChange={(v) => update("cancel_signed_date", v)}
          />
          <div>
            <Label className="text-sm font-semibold">Signature</Label>
            <SignaturePad
              value={data.cancel_signature}
              onChange={(v) => update("cancel_signature", v)}
            />
          </div>
        </div>
        <p className="text-[11px] italic text-neutral-600 mt-2">
          Only sign here if you are formally cancelling this contract.
        </p>
      </div>
    </div>
  );
}
